<template>
  <div class="customer-signin-page-bg">
    <header class="signin-header">
      <div class="header-content">
        <div class="logo-container">
          <img src="@/assets/logo.png" alt="Tartoro Logo" class="header-logo" style="width: 54px; height: 54px;" />
          <span class="header-title" style="color: #000000; font-size: 24px; font-weight: 600;">Tartoro</span>
        </div>
        <div class="language-dropdown" @click="toggleDropdown">
          <span class="globe-icon">🌐</span>
          <span id="selected-language">{{ currentLanguage }}</span>
          <span class="chevron-down">▼</span>
          <div class="dropdown-content" v-show="isDropdownOpen">
            <a href="#" @click.prevent="changeLanguage('en')">English</a>
            <a href="#" @click.prevent="changeLanguage('es')">Español</a>
          </div>
        </div>
      </div>
    </header>
    <div class="customer-signin-container">
      <!-- Left Side: Welcome Section -->
      <div class="signin-left">
        <div class="signin-overlay"></div>
        <div class="signin-welcome">
          <div class="signin-icon">
            <i class="fas fa-lock"></i>
          </div>
          <p class="signin-subtitle">{{ $t('changePassword.welcome.description') }}</p>
          <div class="signin-features-card">
            <ul class="signin-features">
              <li><i class="fas fa-check-circle"></i> {{ $t('changePassword.welcome.features.secure') }}</li>
              <li><i class="fas fa-check-circle"></i> {{ $t('changePassword.welcome.features.easy') }}</li>
              <li><i class="fas fa-check-circle"></i> {{ $t('changePassword.welcome.features.support') }}</li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Right Side: Change Password Form -->
      <div class="signin-right">
        <div class="signin-box">
          <div class="back-button-container">
            <BackButton @back="$router.push('/customer-signin')">{{ $t('common.back') }}</BackButton>
          </div>
          <h2 class="form-title">{{ $t('changePassword.welcome.title') }}</h2>
          <form @submit.prevent="handlePasswordChange" class="signin-form">
            <div class="form-group">
              <label for="newPassword">{{ $t('changePassword.form.newPassword.label') }}</label>
              <div class="input-icon-group">
                <i class="fas fa-lock"></i>
                <input
                  :type="showNewPassword ? 'text' : 'password'"
                  id="newPassword"
                  v-model="newPassword"
                  required
                  :placeholder="$t('changePassword.form.newPassword.placeholder')"
                />
                <i 
                  :class="showNewPassword ? 'fas fa-eye-slash' : 'fas fa-eye'" 
                  @click="showNewPassword = !showNewPassword"
                  class="password-toggle"
                ></i>
              </div>
            </div>

            <div class="form-group">
              <label for="confirmPassword">{{ $t('changePassword.form.confirmPassword.label') }}</label>
              <div class="input-icon-group">
                <i class="fas fa-lock"></i>
                <input
                  :type="showConfirmPassword ? 'text' : 'password'"
                  id="confirmPassword"
                  v-model="confirmPassword"
                  required
                  :placeholder="$t('changePassword.form.confirmPassword.placeholder')"
                />
                <i 
                  :class="showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'" 
                  @click="showConfirmPassword = !showConfirmPassword"
                  class="password-toggle"
                ></i>
              </div>
            </div>

            <div v-if="error" class="error-message">{{ error }}</div>
            <button type="submit" class="signin-button" :disabled="loading">
              {{ loading ? $t('changePassword.form.loading') : $t('changePassword.form.submit') }}
            </button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import authService from '@/services/authService';
import '@/styles/CustomerSignIn.css';

export default {
  name: 'CustomerChangePassword',
  data() {
    return {
      newPassword: '',
      confirmPassword: '',
      showNewPassword: false,
      showConfirmPassword: false,
      error: '',
      loading: false,
      isDropdownOpen: false,
      currentLanguage: 'English'
    }
  },
  methods: {
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen;
    },
    changeLanguage(lang) {
      this.currentLanguage = lang === 'en' ? 'English' : 'Español';
      this.$i18n.locale = lang;
      localStorage.setItem('language', lang);
      this.isDropdownOpen = false;
    },
    async handlePasswordChange() {
      this.error = '';
      
      if (this.newPassword !== this.confirmPassword) {
        this.error = this.$t('changePassword.errors.passwordMismatch');
        return;
      }

      if (this.newPassword.length < 8) {
        this.error = this.$t('changePassword.errors.passwordTooShort');
        return;
      }

      this.loading = true;
      try {
        const email = localStorage.getItem('resetEmail');
        if (!email) {
          throw new Error(this.$t('changePassword.errors.noEmailFound'));
        }

        const response = await authService.resetPassword({
          email,
          newPassword: this.newPassword
        });

        if (response.success) {
          localStorage.removeItem('resetEmail');
          this.$router.push('/signin');
        } else {
          throw new Error(response.message || this.$t('changePassword.errors.resetFailed'));
        }
      } catch (error) {
        this.error = error.message || this.$t('changePassword.errors.resetFailed');
      } finally {
        this.loading = false;
      }
    }
  },
  beforeMount() {
    const email = localStorage.getItem('resetEmail');
    if (!email) {
      this.$router.push('/forgot-password');
    }
  },
  created() {
    const savedLanguage = localStorage.getItem('language') || 'en';
    this.currentLanguage = savedLanguage === 'en' ? 'English' : 'Español';
    this.$i18n.locale = savedLanguage;
  }
}
</script>

<style scoped>
.password-toggle {
  position: absolute;
  right: 30px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  color: #666;
  z-index: 2;
}
.input-icon-group {
  position: relative;
  display: flex;
  align-items: center;
}
.input-icon-group input {
  width: 100%;
  padding-right: 2.2rem;
  padding-left: 2.2rem;
  height: 2.8rem;
  border-radius: 8px;
  border: 1px solid #ccc;
  font-size: 1rem;
  background: #fff;
  transition: border-color 0.2s;
}
.input-icon-group input:focus {
  border-color: #007a1f;
  outline: none;
  box-shadow: 0 0 0 2px rgba(0, 122, 31, 0.08);
}
.input-icon-group i.fas.fa-lock {
  position: absolute;
  left: 12px;
  color: #aaa;
  z-index: 1;
  font-size: 1.1rem;
}
.form-group {
  margin-bottom: 1.5rem;
}
.signin-button {
  margin-top: 1.5rem;
  width: 100%;
  padding: 0.9rem 0;
  font-size: 1.1rem;
  border-radius: 8px;
}
.error-message {
  color: #d32f2f;
  margin-bottom: 0.5rem;
  font-size: 0.98rem;
  text-align: left;
}
</style> 